import React, { JSX, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import Checkbox from "@mui/material/Checkbox";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";

import { isBefore, sort, toDateTimeString } from "utils/DateUtils";
import { asTitle } from "utils/Utils";

import { TrajectoryInfo, TrajectoryRight } from "./TrajectoryService";

export type TrajectoryGroupProps = {
	trajectories: Array<TrajectoryInfo>;
	selected: Array<number>;
	onSelect: (ids: Array<number>, selected: boolean) => void;
};

export default function TrajectoryGroup(
	props: Readonly<TrajectoryGroupProps>
): JSX.Element {
	const { t } = useTranslation();

	const [orderBy, setOrderBy] = useState<
		"name" | "description" | "activeUntil"
	>("name");
	const [sortAsc, setSortAsc] = useState<boolean>(true);

	const sorted = useMemo(
		() =>
			props.trajectories.toSorted((a, b) => {
				switch (orderBy) {
					case "name": {
						return sortByName(a, b);
					}
					case "description": {
						return sortByDescription(a, b);
					}
					case "activeUntil": {
						return sortByAciveUntil(a, b);
					}
					default: {
						return 0;
					}
				}
			}),
		[props.trajectories, orderBy, sortAsc]
	);

	const filteredSelected = useMemo(
		() => props.trajectories.filter(t => props.selected.includes(t.id)),
		[props.trajectories, props.selected]
	);

	function sortByName(a: TrajectoryInfo, b: TrajectoryInfo): number {
		if (sortAsc) {
			return a.name.localeCompare(b.name);
		} else {
			return b.name.localeCompare(a.name);
		}
	}

	function sortByDescription(a: TrajectoryInfo, b: TrajectoryInfo): number {
		// empty values should always be shown in the end.
		if (a.description.trim() === "") {
			return 1;
		}

		if (b.description.trim() === "") {
			return -1;
		}

		if (sortAsc) {
			return a.description.localeCompare(b.description);
		} else {
			return b.description.localeCompare(a.description);
		}
	}

	function sortByAciveUntil(a: TrajectoryInfo, b: TrajectoryInfo): number {
		if (a.activeUntil === null || isBefore(a.activeUntil, 0)) {
			return 1;
		}

		if (b.activeUntil === null || isBefore(b.activeUntil, 0)) {
			return -1;
		}

		if (sortAsc) {
			return sort(a.activeUntil, b.activeUntil);
		} else {
			return sort(b.activeUntil, a.activeUntil);
		}
	}

	function select(id: number, checked: boolean): void {
		if (checked) {
			props.onSelect([id], true);
		} else {
			props.onSelect([id], false);
		}
	}

	function selectAll(event: React.ChangeEvent<HTMLInputElement>): void {
		const ids = props.trajectories
			.filter(t => t.rights.includes(TrajectoryRight.METADATA))
			.map(t => t.id);
		if (event.target.checked) {
			props.onSelect(ids, true);
		} else {
			props.onSelect(ids, false);
		}
	}

	function updateOrderBy(
		newOrderBy: "name" | "description" | "activeUntil"
	): void {
		if (orderBy !== newOrderBy) {
			setOrderBy(newOrderBy);
			setSortAsc(true);
		} else {
			setSortAsc(previous => !previous);
		}
	}

	const orderByName = useCallback((): void => {
		updateOrderBy("name");
	}, [orderBy, sortAsc]);

	const orderByDescription = useCallback((): void => {
		updateOrderBy("description");
	}, [orderBy, sortAsc]);

	const orderByActiveUntil = useCallback((): void => {
		updateOrderBy("activeUntil");
	}, [orderBy, sortAsc]);

	function createExpiryDate(expiryDate: string | null): JSX.Element {
		let content: JSX.Element;
		if (expiryDate === null || isBefore(expiryDate, 0)) {
			content = (
				<Typography sx={{ color: "red" }} variant="body2">
					{t("inactive")}
				</Typography>
			);
		} else if (isBefore(expiryDate, 14)) {
			content = (
				<Typography sx={{ color: "orange" }} variant="body2">
					{toDateTimeString(expiryDate)}
				</Typography>
			);
		} else {
			content = (
				<Typography variant="body2">
					{toDateTimeString(expiryDate)}
				</Typography>
			);
		}

		return content;
	}

	return (
		<Table size="small" aria-label="documents">
			<TableHead>
				<TableRow>
					<TableCell padding="checkbox">
						<Checkbox
							id="select-all"
							indeterminate={
								filteredSelected.length > 0 &&
								filteredSelected.length <
									props.trajectories.length
							}
							checked={
								filteredSelected.length > 0 &&
								filteredSelected.length ===
									props.trajectories.length
							}
							onChange={selectAll}
						/>
					</TableCell>
					<TableCell>
						<TableSortLabel
							active={orderBy === "name"}
							direction={sortAsc ? "asc" : "desc"}
							onClick={orderByName}>
							{asTitle(t("title"))}
						</TableSortLabel>
					</TableCell>
					<TableCell>
						<TableSortLabel
							active={orderBy === "description"}
							direction={sortAsc ? "asc" : "desc"}
							onClick={orderByDescription}>
							{asTitle(t("description"))}
						</TableSortLabel>
					</TableCell>
					<TableCell sx={{ width: 120 }} padding="none" align="right">
						<TableSortLabel
							active={orderBy === "activeUntil"}
							direction={sortAsc ? "asc" : "desc"}
							onClick={orderByActiveUntil}>
							{asTitle(t("activeUntil"))}
						</TableSortLabel>
					</TableCell>
				</TableRow>
			</TableHead>
			<TableBody>
				{sorted.map(row => (
					<TableRow key={row.id} id={"row-" + row.id} hover>
						<TableCell padding="checkbox">
							{row.rights.includes(TrajectoryRight.METADATA) && (
								<Checkbox
									id={"select-" + row.id}
									checked={props.selected.includes(row.id)}
									onChange={(_event, checked): void =>
										select(row.id, checked)
									}
								/>
							)}
						</TableCell>
						<TableCell
							id={"name-" + row.id}
							component="th"
							scope="row">
							<Link to={"/trajectory/" + row.id}>{row.name}</Link>
						</TableCell>
						<TableCell id={"description-" + row.id}>
							{row.description}
						</TableCell>
						<TableCell
							sx={{ width: 120 }}
							padding="none"
							align="right">
							{createExpiryDate(row.activeUntil)}
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}
